// Declares `MuiPickerPopper` on the theme's `components` map.
/// <reference types="@mui/x-date-pickers/themeAugmentation" />
import type {ThemeOptions} from '@mui/material/styles';

import {isMountedInShell} from '../shellRemote';

/**
 * The overlay stacking scale, as integers.
 *
 * `@wandb/components` publishes the scale as CSS custom properties
 * (`--layer-overlay` … `--layer-toast`, see its `theme/layer.css`), offset by
 * hundreds from a single `--layer-base`. Core pins `--layer-base` in
 * `weave/common/css/Base.less`; `LAYER_BASE` here must equal that literal, and
 * the offsets must match the library's. Both are asserted by `layers.test.ts`.
 * This is the only cross-language coupling in the design, so keep it
 * mechanical.
 *
 * Anything that can take a CSS string should use `layer()` from
 * `@wandb/components` instead of these numbers. MUI's theme is the exception:
 * `createTheme({zIndex})` takes numbers, so MUI is classified here.
 */
export const LAYER_BASE = 100000;

export const LAYER = {
  overlay: LAYER_BASE + 0,
  popover: LAYER_BASE + 100,
  menu: LAYER_BASE + 200,
  tooltip: LAYER_BASE + 300,
  toast: LAYER_BASE + 400,
} as const;

/**
 * Core's interim layers for the legacy chrome, as integers. They mirror
 * `--layer-side-panel`, `--layer-chrome` and `--layer-modal` in the Base.less
 * stacking block, which is the canonical write-up; `layers.test.ts` asserts the
 * offsets agree.
 *
 * Standalone, the legacy top nav is `position: fixed` with a z-index, so it
 * needs a slot in the library's overlay → popover gap: side panels slide under
 * it, blocking modals cover it. Under the Forge shell the header is in normal
 * flow with no z-index, so the CSS collapses all three onto `--layer-overlay`
 * and this does the same. Pass `inShell` explicitly only in tests.
 */
export function appLayer(inShell: boolean = isMountedInShell()) {
  return {
    sidePanel: LAYER.overlay,
    chrome: inShell ? LAYER.overlay : LAYER.overlay + 40,
    modal: inShell ? LAYER.overlay : LAYER.overlay + 80,
  } as const;
}

/**
 * Level 1 of the MUI adapter: MUI's theme `zIndex` keys.
 *
 * MUI collapses Dialog, temporary Drawer, Popover, Menu, Autocomplete and the
 * date-picker popper onto `modal`. Dialogs are blocking modals, temporary
 * Drawers are side panels; the rest are split off onto `menu` by
 * `MUI_LAYER_COMPONENTS`.
 */
export function muiZIndex(inShell: boolean = isMountedInShell()) {
  const app = appLayer(inShell);
  return {
    drawer: app.sidePanel,
    modal: app.modal,
    snackbar: LAYER.toast,
    tooltip: LAYER.tooltip,
  } satisfies NonNullable<ThemeOptions['zIndex']>;
}

/**
 * Level 2 of the MUI adapter: the non-dialog overlays that MUI reads off
 * `theme.zIndex.modal`, moved onto the menu layer via `styleOverrides`.
 * `MuiPickerPopper` is the `@mui/x-date-pickers` desktop popper.
 */
export const MUI_LAYER_COMPONENTS = {
  MuiPopover: {styleOverrides: {root: {zIndex: LAYER.menu}}},
  MuiMenu: {styleOverrides: {root: {zIndex: LAYER.menu}}},
  MuiAutocomplete: {styleOverrides: {popper: {zIndex: LAYER.menu}}},
  MuiPickerPopper: {styleOverrides: {root: {zIndex: LAYER.menu}}},
} as const satisfies NonNullable<ThemeOptions['components']>;

/** `MUI_LAYER_THEME` for a given host; the constant below is the live one. */
export function muiLayerTheme(
  inShell: boolean = isMountedInShell()
): ThemeOptions {
  return {zIndex: muiZIndex(inShell), components: MUI_LAYER_COMPONENTS};
}

/**
 * Pass as the second argument to every `createTheme(options, MUI_LAYER_THEME)`
 * so it deep-merges over the site's own options. A theme built from scratch
 * without it resets `zIndex` to MUI's defaults (1200–1500) and its overlays
 * fall behind every `@wandb/components` overlay that contains them.
 *
 * Resolved once at module load: the shell defines its config before it loads
 * the remote, and standalone never defines it.
 */
export const MUI_LAYER_THEME: ThemeOptions = muiLayerTheme();
